import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import ValidatorPage from "./pages/ValidatorPage";
import AdminPage from "./pages/AdminPage";
import AdminValidationsPage from "./pages/AdminValidationsPage";
import SeoPage from "./pages/SeoPage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function EmailVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // "verifying" | "success" | "error"
  const [message, setMessage] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get("verify");
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    axios.post(`${API}/auth/verify-email?token=${token}`, {}, { withCredentials: true })
      .then((res) => {
        setStatus("success");
        setMessage("Votre email a été vérifié avec succès !");
        if (res.data.user_id) {
          setUser({
            user_id: res.data.user_id,
            email: res.data.email,
            name: res.data.name,
            is_premium: res.data.is_premium,
            role: res.data.role,
            email_verified: true
          });
        }
        // Redirect after 2 seconds
        setTimeout(() => navigate("/", { replace: true }), 2000);
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Lien de vérification invalide ou expiré.");
      });
  }, [searchParams, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
        {status === "verifying" && (
          <>
            <div className="h-12 w-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Vérification en cours...</h2>
            <p className="text-sm text-slate-600">Veuillez patienter</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-emerald-700 mb-2">{message}</h2>
            <p className="text-sm text-slate-600">Redirection vers la page principale...</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-red-700 mb-2">Erreur de vérification</h2>
            <p className="text-sm text-slate-600 mb-4">{message}</p>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retour à l'accueil
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function AuthCallback() {
  const { processGoogleCallback } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
    if (sessionId) {
      processGoogleCallback(sessionId)
        .then(() => navigate("/", { replace: true }))
        .catch(() => navigate("/", { replace: true }));
    } else {
      navigate("/", { replace: true });
    }
  }, [processGoogleCallback, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-slate-600">Connexion en cours...</p>
      </div>
    </div>
  );
}

function AdminWrapper() {
  const navigate = useNavigate();
  return <AdminPage onBack={() => navigate("/")} />;
}

function AdminValidationsWrapper() {
  const navigate = useNavigate();
  return <AdminValidationsPage onBack={() => navigate("/admin")} />;
}

function AppRouter() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  // Check for email verification token
  if (searchParams.get("verify")) {
    return <EmailVerification />;
  }
  
  // Check for Google OAuth callback
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<ValidatorPage />} />
      <Route path="/ubl" element={<SeoPage pageKey="ubl" />} />
      <Route path="/factur-x" element={<SeoPage pageKey="facturx" />} />
      <Route path="/cii" element={<SeoPage pageKey="cii" />} />
      <Route path="/cdar" element={<SeoPage pageKey="cdar" />} />
      <Route path="/admin" element={<AdminWrapper />} />
      <Route path="/admin/validations" element={<AdminValidationsWrapper />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
